// One date grammar for the whole app. Absolute dates render as "Jul 12, 2026"
// (with time as "Jul 12, 2026, 09:41" when needed); relative ages as "4d ago"
// (allowed only on list cards, always paired with the absolute form as a
// title/tooltip). Ambiguous numeric dd/mm formats are banned — never render a
// bare `toLocaleDateString()`/`toLocaleString()` at a call site; go through here.

/** Anything a call site might hold for a timestamp. */
export type DateInput = Date | string | number | null | undefined;

/**
 * Coerce a `DateInput` to a valid `Date`, or `null` when it isn't usable. A
 * date-only string ("2026-07-12") becomes local midnight of that calendar day.
 */
export function toDate(input: DateInput): Date | null {
  if (input === null || input === undefined || input === "") return null;
  let d: Date;
  if (input instanceof Date) {
    d = input;
  } else if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    // A date-only string ("2026-07-12") is parsed as UTC midnight by
    // `new Date(...)`, which renders as the PREVIOUS day for anyone west of UTC.
    // Construct it from local calendar components so it means the local day.
    const [y, m, day] = input.split("-").map(Number);
    d = new Date(y, m - 1, day);
  } else {
    d = new Date(input);
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

const ABS_DATE_OPTS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

const ABS_TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

// Build YYYY-MM-DD from LOCAL calendar components (not `toISOString()`, which
// uses the UTC day). Near midnight the UTC day can differ from the user's local
// day, which would shift "last N days" boundaries by one.
export const formatDateYYYYMMDD = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * Absolute date, e.g. "Jul 12, 2026". Empty string for an unusable input so
 * call sites can render it inline without guarding.
 *
 * @example formatDate("2026-07-12T09:41:40Z") // "Jul 12, 2026"
 * @example formatDate(null)                   // ""
 */
export function formatDate(input: DateInput): string {
  const d = toDate(input);
  if (!d) return "";
  return d.toLocaleDateString("en-US", ABS_DATE_OPTS);
}

/**
 * Absolute date + 24h time, e.g. "Jul 12, 2026, 09:41". Use where the moment
 * matters (a header "last updated" stamp, an admin feedback row) instead of the
 * ambiguous dd/mm `toLocaleString()`.
 *
 * @example formatDateTime("2026-07-12T09:41:40Z") // "Jul 12, 2026, 09:41"
 */
export function formatDateTime(input: DateInput): string {
  const d = toDate(input);
  if (!d) return "";
  const date = d.toLocaleDateString("en-US", ABS_DATE_OPTS);
  const time = d.toLocaleTimeString("en-US", ABS_TIME_OPTS);
  return `${date}, ${time}`;
}

/**
 * Mark an offset-less ISO date-time ("2026-09-28T09:59:00") as UTC by appending
 * "Z"; anything else passes through. The backend emits naive UTC, which
 * `new Date(...)` would otherwise read as local time.
 *
 * ponytail: opt-in per call site (Context Console only) and assumes the
 * deployed backend runs in UTC; other screens still read naive strings as
 * local. Real fix: backend emits offsets.
 */
export function naiveAsUtc(input: DateInput): DateInput {
  return typeof input === "string" && /^\d{4}-\d{2}-\d{2}T[\d:.]+$/.test(input) ? `${input}Z` : input;
}

const ABS_TIME_TZ_OPTS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZoneName: "short",
};

/**
 * Absolute date + time + the viewer's own timezone abbreviation, e.g.
 * "Jul 12, 2026, 09:41 GMT+5:30". Use wherever a timestamp is the one thing
 * on the screen the viewer needs to read unambiguously (a "published at"
 * receipt, an audit stamp) — {@link formatDateTime} omits the zone, which is
 * fine for a list card but not for a value someone might act on.
 *
 * An offset-less datetime string is read as UTC, not local time: the backend
 * emits naive UTC ("2026-09-28T09:59:00", no "Z"), and `new Date(...)` would
 * otherwise show a 15:29 IST publish as "09:59 GMT+5:30".
 *
 * @example formatDateTimeWithTz("2026-07-12T09:41:40Z") // "Jul 12, 2026, 14:41 GMT+5:30"
 * @example formatDateTimeWithTz("2026-07-12T09:41:40")  // same as above
 */
export function formatDateTimeWithTz(input: DateInput): string {
  const d = toDate(naiveAsUtc(input));
  if (!d) return "";
  const date = d.toLocaleDateString("en-US", ABS_DATE_OPTS);
  const time = d.toLocaleTimeString("en-US", ABS_TIME_TZ_OPTS);
  return `${date}, ${time}`;
}

/**
 * Compact relative age — "just now", "5m ago", "3h ago", "2d ago", "4w ago",
 * "6mo ago", "2y ago". Empty when no usable timestamp. Coarse buckets keep card
 * meta glanceable. Allowed ONLY on list cards, and always paired with the
 * absolute form ({@link formatDate}/{@link formatDateTime}) as a title/tooltip.
 *
 * @param now injectable "current" epoch ms, for deterministic tests.
 * @example formatRelativeTime(threeDaysAgoIso) // "3d ago"
 */
export function formatRelativeTime(input: DateInput, now: number = Date.now()): string {
  const d = toDate(input);
  if (!d) return "";
  const sec = Math.round((now - d.getTime()) / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.round(day / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.round(day / 365);
  return `${yr}y ago`;
}
